/**
 * Card Showcase - Hero section with full-width card image and card identity
 *
 * Shared by all card details panels. Collection cards additionally show the copy's language.
 */
import clsx from 'clsx';
import type { ReactElement, ReactNode } from 'react';
import { useMemo, useState } from 'react';

import CardImage from '@/components/CardImage/CardImage';
import KeywordIcons from '@/components/KeywordIcons/KeywordIcons';
import { TextWithMana } from '@/components/ManaSymbol/ManaSymbol';
import SplitManaCost from '@/components/ManaSymbol/SplitManaCost';
import RarityBadge from '@/components/RarityBadge/RarityBadge';
import RoleTags from '@/components/RoleTags/RoleTags';
import SetIcon from '@/components/SetIcon/SetIcon';
import { classifyCard } from '@/lib/cardClassification';
import { LANGUAGE_LABELS } from '@/lib/cardUtils';
import type { CardFace, EnrichedCollectionCard, ImageUris, ScryfallCard } from '@/types/card';

export interface CardShowcaseProps {
  /** Card to display - accepts either full EnrichedCollectionCard or just ScryfallCard */
  card: EnrichedCollectionCard | ScryfallCard;
  /** Shows a "Change edition" link next to the set name */
  onChangeEdition?: () => void;
}

/** Type guard to check if card is EnrichedCollectionCard */
function isEnrichedCard(card: EnrichedCollectionCard | ScryfallCard): card is EnrichedCollectionCard {
  return 'scryfall_id' in card && 'card' in card;
}

/** Card layouts where each face has its own image (toggle between faces) */
const MULTI_FACE_LAYOUTS = ['transform', 'modal_dfc', 'meld', 'reversible_card', 'double_faced_token', 'art_series'];

/** Card layouts where a single image shows all faces (render all face text together) */
const COMBINED_FACE_LAYOUTS = ['split', 'adventure', 'flip'];

/**
 * Parse and render oracle text with line breaks and mana symbols
 */
function renderOracleText(text: string): ReactElement {
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, i) => (
        <p key={i} className={i > 0 ? 'mt-2' : ''}>
          <TextWithMana text={line} />
        </p>
      ))}
    </>
  );
}

function Separator(): ReactElement {
  return (
    <span className="text-ash" aria-hidden="true">
      ·
    </span>
  );
}

function StatsBadges({ stats }: { stats: string[] }): ReactElement {
  return (
    <div className="mt-2 flex justify-end gap-2">
      {stats.map((stat) => (
        <span key={stat} className="border-storm bg-obsidian/60 text-pearl rounded-md border px-2 py-0.5 font-mono text-sm font-medium">
          {stat}
        </span>
      ))}
    </div>
  );
}

function TagRow({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="flex items-start gap-3">
      <dt className="text-ash w-16 shrink-0 pt-1 text-[10px] font-medium tracking-widest uppercase">{label}</dt>
      <dd className="min-w-0 flex-1">{children}</dd>
    </div>
  );
}

export default function CardShowcase({ card, onChangeEdition }: CardShowcaseProps): ReactElement {
  const [activeFace, setActiveFace] = useState(0);

  // Extract ScryfallCard from either input type
  const enriched = isEnrichedCard(card) ? card : null;
  const scryfallCard = enriched ? enriched.card : (card as ScryfallCard);
  const roles = useMemo(() => enriched?.classification.active_roles ?? classifyCard(scryfallCard).active_roles, [enriched, scryfallCard]);
  const languageLabel = enriched ? LANGUAGE_LABELS[enriched.language] : null;
  const hasMultipleFaces =
    MULTI_FACE_LAYOUTS.includes(scryfallCard.layout) && scryfallCard.card_faces && scryfallCard.card_faces.length > 1;
  const hasCombinedFaces =
    COMBINED_FACE_LAYOUTS.includes(scryfallCard.layout) && !!scryfallCard.card_faces && scryfallCard.card_faces.length > 1;

  // Get image URIs for current face
  const getImageUris = (): ImageUris | null => {
    if (hasMultipleFaces && scryfallCard.card_faces?.[activeFace]?.image_uris) {
      return scryfallCard.card_faces[activeFace].image_uris;
    }
    return scryfallCard.image_uris;
  };

  // Get face data for display
  const getCurrentFace = (): CardFace | null => {
    if (hasMultipleFaces && scryfallCard.card_faces?.[activeFace]) {
      return scryfallCard.card_faces[activeFace];
    }
    return null;
  };

  const imageUris = getImageUris();
  const currentFace = getCurrentFace();

  // Use face data if available, otherwise use main card data
  const displayName = currentFace?.name ?? scryfallCard.name;
  const displayManaCost = currentFace?.mana_cost ?? scryfallCard.mana_cost;
  const displayTypeLine = currentFace?.type_line ?? scryfallCard.type_line;
  const displayArtist = currentFace?.artist ?? scryfallCard.artist;
  const displayOracleText = currentFace?.oracle_text ?? scryfallCard.oracle_text;

  // Build stats line based on card type
  const stats: string[] = [];
  const power = currentFace?.power ?? scryfallCard.power;
  const toughness = currentFace?.toughness ?? scryfallCard.toughness;
  const loyalty = currentFace?.loyalty ?? scryfallCard.loyalty;
  const defense = currentFace?.defense ?? scryfallCard.defense;

  if (power && toughness) {
    stats.push(`${power}/${toughness}`);
  }
  if (loyalty) {
    stats.push(`Loyalty: ${loyalty}`);
  }
  if (defense) {
    stats.push(`Defense: ${defense}`);
  }

  const keywords = scryfallCard.keywords ?? [];
  const hasTags = roles.length > 0 || keywords.length > 0;

  return (
    <div className="p-6">
      {/* Card Image - full width */}
      <div className="mb-5">
        <div className="shadow-void/40 overflow-hidden rounded-xl shadow-xl">
          <CardImage imageUris={imageUris} name={displayName} size="fluid" sourceSize="large" />
        </div>

        {/* Face Toggle for DFCs */}
        {hasMultipleFaces && scryfallCard.card_faces && (
          <div className="mt-3 flex justify-center gap-1">
            {scryfallCard.card_faces.map((face, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setActiveFace(index)}
                aria-pressed={activeFace === index}
                className={clsx(
                  'focus-ring rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  activeFace === index ? 'bg-aether text-pearl' : 'bg-slate text-silver hover:text-pearl',
                )}
              >
                {face.name.split(' //')[0]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Card Identity + Info - Below image */}
      <div className="bg-slate/50 border-storm rounded-xl border p-4">
        {/* Card Name + Mana Cost (combined-face cards show cost and type per face instead) */}
        <div className="mb-1 flex items-start justify-between gap-3">
          <h2 id="card-details-title" className="font-heading text-pearl text-lg font-semibold tracking-wide">
            {displayName}
          </h2>
          {displayManaCost && !hasCombinedFaces && (
            <div className="mt-1 shrink-0">
              <SplitManaCost cost={displayManaCost} />
            </div>
          )}
        </div>

        {!hasCombinedFaces && <p className="text-silver text-sm">{displayTypeLine}</p>}

        {/* Oracle Text */}
        {hasCombinedFaces && scryfallCard.card_faces ? (
          <>
            <div className="mt-3 flex flex-col gap-3">
              {scryfallCard.card_faces.map((face, i) => (
                <div key={i} className="bg-slate/40 border-storm/60 rounded-lg border p-3">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <h3 className="font-heading text-pearl text-sm font-semibold tracking-wide">{face.name}</h3>
                    {face.mana_cost && <SplitManaCost cost={face.mana_cost} />}
                  </div>
                  {face.type_line && <p className="text-silver mb-2 text-xs">{face.type_line}</p>}
                  {face.oracle_text && <div className="text-silver text-sm leading-relaxed">{renderOracleText(face.oracle_text)}</div>}
                  {face.power && face.toughness && <StatsBadges stats={[`${face.power}/${face.toughness}`]} />}
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {displayOracleText && (
              <>
                <div className="bg-storm my-3 h-px" />
                <div className="text-silver text-sm leading-relaxed">{renderOracleText(displayOracleText)}</div>
              </>
            )}
            {stats.length > 0 && <StatsBadges stats={stats} />}
          </>
        )}

        <div className="bg-storm my-3 h-px" />

        {/* Printing */}
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center gap-2">
            <SetIcon setCode={scryfallCard.set} rarity={scryfallCard.rarity} size="md" hideFallback />
            <span className="text-silver min-w-0 flex-1 truncate" title={scryfallCard.set_name}>
              {scryfallCard.set_name}
            </span>
            {onChangeEdition && (
              <button
                type="button"
                onClick={onChangeEdition}
                className="text-aether hover:text-pearl focus-ring shrink-0 rounded px-1 text-xs font-medium transition-colors"
              >
                Change edition
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="bg-storm text-silver rounded px-1.5 py-0.5 font-mono text-xs uppercase">
              {scryfallCard.set} #{scryfallCard.collector_number}
            </span>
            <Separator />
            <RarityBadge rarity={scryfallCard.rarity} variant="text" />
            {languageLabel && (
              <>
                <Separator />
                <span className="text-silver">{languageLabel}</span>
              </>
            )}
            {scryfallCard.game_changer === true && (
              <span className="bg-gold/15 text-gold rounded px-2 py-0.5 text-xs font-medium">Game Changer</span>
            )}
          </div>
          {(displayArtist || scryfallCard.edhrec_rank != null) && (
            <div className="text-ash flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate italic">{displayArtist && `Illus. ${displayArtist}`}</span>
              {scryfallCard.edhrec_rank != null && <span className="shrink-0">EDHREC #{scryfallCard.edhrec_rank}</span>}
            </div>
          )}
        </div>

        {/* Roles + Keywords */}
        {hasTags && (
          <>
            <div className="bg-storm my-3 h-px" />
            <dl className="space-y-2">
              {roles.length > 0 && (
                <TagRow label="Roles">
                  <RoleTags roles={roles} maxVisible={roles.length} />
                </TagRow>
              )}
              {keywords.length > 0 && (
                <TagRow label="Keywords">
                  <KeywordIcons keywords={keywords} iconOnly={false} maxVisible={99} />
                </TagRow>
              )}
            </dl>
          </>
        )}
      </div>
    </div>
  );
}
